/**
 * Shared axios instance for the src/queries/* TanStack Query hooks.
 * Replaces hook/services.js's per-endpoint CallService lookup table.
 *
 * env.API_URL is only populated once loadConfig() resolves in src/index.js's
 * bootstrap(), *before* ReactDOM renders - so it's always ready by the time
 * any query/mutation hook actually runs. Read per-request via an interceptor
 * (not baked into `baseURL` at module-load time) so this stays correct even
 * if a query hook module is ever imported earlier than expected.
 *
 * Auth token attachment lives here, not in every call site, same reasoning
 * as social-calendar's apiClient: most hooks call apiClient.request(...)
 * directly, so a per-call `token` option would silently miss almost every
 * request without a global interceptor.
 *
 * On a 401, this app has no /auth/sign-in route (it boots from an encrypted
 * email:locationId URL segment, not a login page) - mirrors the existing
 * useLogout() in hook/auth.js instead: clear storage, send the user to /404.
 */
import axios from "axios";
import localforage from "localforage";
import env from "config/env";

const apiClient = axios.create();

apiClient.interceptors.request.use(async (config) => {
  config.baseURL = `${env.API_URL}/v1`;
  if (!config.headers?.["x-auth-token"]) {
    const token = await localforage.getItem("token");
    if (token) {
      config.headers = { ...config.headers, "x-auth-token": `Bearer ${token}` };
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      await localforage.clear();
      window.location.replace("/404");
    }
    return Promise.reject(error);
  },
);

export default apiClient;
